import { useEffect, useRef, type ReactNode } from "react";

interface SignatureFrameProps {
  title: string;
  meta: string;
  code: string;
  children: ReactNode;
  /** Extra class on the wrapper, for context-specific variants. */
  className?: string;
}

/**
 * Wrapper for signature illustrations. Adds the `.in` class on the first time
 * the frame intersects the viewport, which kicks off the draw-on-scroll
 * animations defined in styles/signature.css.
 */
export function SignatureFrame({ title, meta, code, children, className = "" }: SignatureFrameProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      // Reveal as soon as any sliver enters, pre-triggering a little before the
      // frame scrolls into view. A higher threshold left frames that settle only
      // partially on screen (e.g. after an in-page anchor jump, or on short
      // viewports) permanently at opacity:0 — every shape inside is .illo-pop,
      // which only un-hides once the frame gains .in.
      { threshold: 0, rootMargin: "0px 0px 200px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className={"signature-illo" + (className ? " " + className : "")} ref={ref}>
      <div className="signature-illo-frame">{children}</div>
      <div className="signature-illo-cap">
        <span className="signature-illo-cap-title">{title}</span>
        <span>
          {code} · {meta}
        </span>
      </div>
    </div>
  );
}
